import { Button } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"
import { isLockedToLegacy, ProjectPhase } from "authoring/lib/enum/project-phase.mjs"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { getLegacyScheduleSplit } from "../lib/get-legacy-schedule-split"
import { sendProjectToLegacy } from "../controller/send-project-to-legacy"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { SentToLegacyDialog } from "authoring/routes/project/components/sent-to-legacy-dialog"
import { SendToLegacyBlockersDialog } from "../components/send-to-legacy-blockers-dialog"
import { SendToLegacyOptionsDialog } from "../components/send-to-legacy-options-dialog"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_PUSH_TO_LEGACY_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function SendToLegacyButton() {
    const { target: project } = useBoundContext()
    const scheduleIds = project?.scopedScheduleIds ?? []
    const scheduleCount = scheduleIds.length

    const { phase } = project
    const locked = isLockedToLegacy(phase)
    const status = locked ? "sent" : "send"

    const sendToLegacyBlockersDialog = useDialog(<SendToLegacyBlockersDialog />, { maxWidth: "md" })
    const sendToLegacyOptionsDialog = useDialog(<SendToLegacyOptionsDialog />, { maxWidth: "md" })
    const sentToLegacyDialog = useDialog(<SentToLegacyDialog />)

    if (phase === ProjectPhase.PUBLISHED_TO_LEGACY) return null
    if (project?.archived) return null
    return (
        <Secure demand={AUTHORING_PUSH_TO_LEGACY_DEMAND} fallback={<Frag />}>
            <Button
                data-cy={`${status}-to-legacy-button`}
                aria-locked={locked}
                startIcon={locked && <Iconify icon={"material-symbols:lock"} />}
                variant={locked ? "outlined" : "contained"}
                onClick={handleClick}
            >
                {`${status.titleize()} to Legacy`}
            </Button>
        </Secure>
    )

    async function handleClick() {
        if (locked) {
            await sentToLegacyDialog()
            return
        }

        const split = await getLegacyScheduleSplit({ projectId: project?.id, scheduleIds })
        if (!split) {
            errorSnackbar("Could not check the schedules in this work package. Please try again.")
            return
        }

        const { approved, blocking, alreadyPublished } = split

        if (!approved.length || blocking.length || alreadyPublished.length) {
            const confirmed = await sendToLegacyBlockersDialog({
                blockingSchedules: blocking,
                alreadyPublishedSchedules: alreadyPublished,
                scheduleCount,
                approvedCount: approved.length,
            })
            if (!confirmed) return
        }

        const options = await sendToLegacyOptionsDialog({ approvedSchedules: approved })
        if (!options) return

        const result = await sendProjectToLegacy({
            projectId: project?.id,
            incrementVersion: options.incrementVersion,
            publish: false,
        })
        if (!result?.success) {
            errorSnackbar(result?.error || "Changes were not successful")
            return
        }

        successSnackbar("Approved drafts sent to legacy successfully")
        for (const warning of result?.warnings ?? []) {
            warningSnackbar(warning)
        }
    }
}
